import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  untracked,
} from '@angular/core';
import { User, UserStore } from '../../../state/user.repository';
import { UserAvatar } from '../../../core/components/user-avatar/user-avatar';
import { UserName } from '../../../shared/components/user/user-name/user-name';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faPresentationScreen,
  faRepeat,
  faStar,
  faUpRightAndDownLeftFromCenter,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { faEllipsis } from '@fortawesome/pro-duotone-svg-icons';
import { faExclamation, faVideoSlash } from '@fortawesome/pro-regular-svg-icons';
import { IconBtn } from '../../../shared/components/ui/buttons/icon-btn/icon-btn';
import { AudioActivityService } from '../../../core/services/audio-activity.service';
import { UserActivationService } from '../../../core/services/user-activation.service';
import { map, scan, switchMap, tap } from 'rxjs';
import { ParticipantMiniProfile } from '../../../core/components/participant-mini-profile/participant-mini-profile';
import { MuteStatus } from '../../../core/components/mute-status/mute-status';
import { RaisedHandBadge } from '../../../core/components/raised-hand-badge/raised-hand-badge';
import { ContextMenu } from '../../../shared/components/ui/context-menu/context-menu';
import { ContextMenuTriggerDirective } from '../../../shared/components/ui/context-menu/context-menu-trigger.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { DoubleTapTracker } from '../../../shared/helpers/double-tap';
import { NativeVideoPipe } from '../../../shared/pipes/native-video.pipe';
import {
  isNativeVideoStream,
  VideoMountDirective,
} from '../../../shared/directives/video-mount.directive';
import { TooltipDirective } from '../../../shared/components/ui/tooltip/tooltip.directive';
import { OdinService } from '../../../core/services/odin.service';
import { StatsTarget } from '../../../core/services/stream-stats.service';
import {
  healthStateLabel,
  severityRank,
  StreamHealth,
  StreamHealthService,
  StreamKind,
} from '../../../core/services/stream-health.service';
import { StreamStatsDialogService } from '../stream-stats/stream-stats-dialog.service';

interface ScanState {
  prev: User | undefined;
  curr: User | undefined;
}

/**
 * A stream shown in the tile. `isScreenShare` follows the slot, not the
 * source: after "switch streams" the camera sits in the screen-share slot.
 * `source` names the stream actually shown there.
 */
type TileStream = {
  stream: MediaStream | null;
  isScreenShare: boolean;
  source: StreamKind | null;
};

/** What the error overlay on the main video says. */
type HealthOverlay = 'no-media' | 'stalled' | 'failed' | 'no-capture';

const IS_BRAVE = 'brave' in navigator;

@Component({
  selector: 'andor-video-tile',
  standalone: true,
  imports: [
    NativeVideoPipe,
    VideoMountDirective,
    UserAvatar,
    UserName,
    MuteStatus,
    RaisedHandBadge,
    FaIconComponent,
    IconBtn,
    ParticipantMiniProfile,
    ContextMenu,
    ContextMenuTriggerDirective,
    TooltipDirective,
    TranslatePipe,
  ],
  templateUrl: './video-tile.html',
  styleUrl: './video-tile.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoTile implements OnDestroy {
  private userStore = inject(UserStore);
  private audioActivity = inject(AudioActivityService);
  private odin = inject(OdinService);
  private streamHealth = inject(StreamHealthService);
  private streamStatsDialog = inject(StreamStatsDialogService);
  userId = input.required<string>();

  user = toSignal(
    toObservable(this.userId).pipe(
      switchMap((userId) =>
        this.userStore.get$(userId).pipe(
          scan<User | undefined, ScanState>((acc, curr) => ({ prev: acc.curr, curr: curr }), {
            prev: undefined,
            curr: undefined,
          }),

          tap(({ prev, curr }) => {
            // Only the first emission and stream-relevant changes re-run the
            // stream handling; running it on every store update spawns
            // overlapping handleStreams calls whose stale results can
            // overwrite a newer run's streams with null
            if (curr && (!prev || this.streamsChanged(prev, curr))) {
              this.handleStreams(curr).catch((e) =>
                console.warn('failed to update video tile streams', e)
              );
            }
          }),

          map((data) => data.curr),
          // When a user disconnects, keep emitting the last known value so the tile
          // content stays visible until the throttled visible list removes the tile.
          scan<User | undefined, User | undefined>(
            (lastKnown, curr) => curr ?? lastKnown,
            undefined
          )
        )
      )
    )
  );
  // Pinned tiles hide the picture-in-picture stream.
  mode = input.required<'main' | 'pinned'>();
  // Staggers parallel tile mounts so they don't all bind srcObject in the
  // same animation frame — the parent passes `$index * 75` so each
  // subsequent tile starts its WebMediaPlayer slightly later.
  attachDelay = input<number>(0);
  // Whether this tile is shown large (the presentation), and whether the grid
  // offers the stage toggle at all.
  staged = input(false);
  stageable = input(false);
  streamsSwapped = signal(false);
  // Keeps the action pills shown while the participant menu is open: its
  // panel is portaled to <body>, so the tile loses :hover.
  profileMenuOpen = signal(false);
  fullscreenRequested = output<User>();
  stageToggled = output<User>();
  // The screen share when there is one, otherwise the camera.
  mainStream = signal<TileStream>({ stream: null, isScreenShare: false, source: null });
  // The camera, shown small over a screen share.
  pipStream = signal<MediaStream | null>(null);
  protected readonly faEllipsis = faEllipsis;
  protected readonly faUpRightAndDownLeftFromCenter = faUpRightAndDownLeftFromCenter;
  protected readonly faRepeat = faRepeat;
  protected readonly faPresentationScreen = faPresentationScreen;
  protected readonly faXmark = faXmark;
  protected readonly faStar = faStar;
  protected readonly faExclamation = faExclamation;
  protected readonly faVideoSlash = faVideoSlash;
  private _abortController = new AbortController();
  private _userActivation = inject(UserActivationService);
  speaking = computed(() => this.audioActivity.audioActiveFor(this.userId())());

  // The streams whose health this tile watches. Re-evaluated once a started
  // stream is bound (a remote track only exists after start), but compared
  // by stream identity: other user updates must not restart the lifecycle.
  private readonly statsTargets = computed(
    () => {
      this.mainStream();
      const user = this.user();
      const targets: { kind: StreamKind; target: StatsTarget }[] = [];
      if (user?.screenCastStream) {
        targets.push({
          kind: 'screen',
          target: this.odin.videoStatsTarget(user.screenCastStream),
        });
      }
      if (user?.camStream) {
        targets.push({ kind: 'cam', target: this.odin.videoStatsTarget(user.camStream) });
      }
      return targets;
    },
    {
      equal: (a, b) =>
        a.length === b.length &&
        a.every(
          (t, i) =>
            t.kind === b[i].kind &&
            t.target.mediaId === b[i].target.mediaId &&
            t.target.trackId === b[i].target.trackId
        ),
    }
  );

  private health(kind: StreamKind): StreamHealth | null {
    const userId = this.userId();
    return (
      this.streamHealth.streams().find((s) => s.userId === userId && s.kind === kind)?.health ??
      null
    );
  }

  /** The tile's stream in the worst warn or error state, if any. */
  protected readonly problem = computed(() => {
    let worst: { kind: StreamKind; health: StreamHealth } | null = null;
    for (const { kind } of this.statsTargets()) {
      const health = this.health(kind);
      if (severityRank(health) > severityRank(worst?.health)) worst = { kind, health: health! };
    }
    return worst && { ...worst, label: healthStateLabel(worst.health.state) };
  });

  /**
   * Covers the main video while it shows no picture. Network trouble of the
   * own streams doesn't: the local preview keeps rendering regardless.
   */
  protected readonly overlay = computed<{ kind: HealthOverlay; secs: number | null } | null>(() => {
    const source = this.mainStream().source;
    const h = source && this.health(source);
    if (!h) return null;
    switch (h.state) {
      case 'no-capture':
        return { kind: 'no-capture', secs: null };
      case 'no-media':
        return { kind: 'no-media', secs: Math.round(h.watchedForMs / 1000) };
      case 'stalled':
        return h.direction === 'send'
          ? null
          : { kind: 'stalled', secs: Math.round((h.lastPacketAgoMs ?? h.sinceMs) / 1000) };
      case 'failed':
        return { kind: 'failed', secs: null };
      default:
        return null;
    }
  });

  constructor() {
    effect((onCleanup) => {
      const userId = this.userId();
      const targets = this.statsTargets();
      untracked(() => {
        for (const t of targets) this.streamHealth.watch(userId, t.kind, t.target);
      });
      onCleanup(() => {
        for (const t of targets) this.streamHealth.unwatch(userId, t.kind);
      });
    });
  }

  protected openStats(kind: StreamKind) {
    this.streamStatsDialog.open(this.userId(), kind);
  }

  ngOnDestroy(): void {
    this._abortController.abort();
    const user = this.user();
    if (user && user.screenCastStream && user.screenCastStream.kind === 'video-output') {
      user.screenCastStream.pause();
    }
    if (user && user.camStream && user.camStream.kind === 'video-output') {
      user.camStream.pause();
    }
  }

  // Increments per handleStreams run; a run only publishes its result while
  // it is still the newest one (see guard before the stream signal writes)
  private _streamsRun = 0;

  async handleStreams(user: User) {
    const run = ++this._streamsRun;
    let cam: MediaStream | undefined;
    let screen: MediaStream | undefined;
    let camSource: StreamKind = 'cam';
    let screenSource: StreamKind = 'screen';
    if (user.camStream) {
      if (user.camStream.kind === 'video-output') {
        await user.camStream.start();
        if (user.camStream.isPaused) {
          await user.camStream.resume();
        }
      }
      cam = user.camStream?.mediaStream;
    }
    if (user.screenCastStream) {
      if (user.screenCastStream.kind === 'video-output') {
        await user.screenCastStream.start();
        if (user.screenCastStream.isPaused) {
          await user.screenCastStream.resume();
        }
      }
      screen = user.screenCastStream?.mediaStream;
    }

    if (this.streamsSwapped()) {
      [cam, screen] = [screen, cam];
      [camSource, screenSource] = [screenSource, camSource];
    }

    // Delay binding srcObject until the user has activated the page. On
    // persistAppState reload the conference panel mounts before any user
    // gesture; if Chrome creates the WebMediaPlayer for a remote MediaStream
    // in that pre-activation window with the track still muted, the player
    // gets stuck and never paints frames. Resolves immediately once
    // activation has happened.
    await this._userActivation.waitForActivation();

    // Brave needs a small settle window after the user gesture before its
    // WebMediaPlayer can accept a srcObject without getting stuck — only
    // the first bind after activation hits that bug, so skip it afterwards
    // and on other browsers. `attachDelay` still staggers parallel mounts
    // to avoid a burst of WebMediaPlayer creations in the same frame. Then
    // wait for any still-muted tracks to deliver their first frame (3s
    // safety net so a never-unmuting track doesn't hang the bind).
    const needsBraveSettle = IS_BRAVE && !this._userActivation.hasBoundOnce;
    const settleDelay = (needsBraveSettle ? 100 : 0) + this.attachDelay();
    await Promise.all([
      ...(settleDelay > 0
        ? [new Promise<void>((resolve) => setTimeout(resolve, settleDelay))]
        : []),
      // Native streams are placeholders without playable tracks; their
      // frames reach the canvas through the plugin instead.
      ...[cam, screen]
        .filter((s) => !isNativeVideoStream(s))
        .map((s) => s?.getVideoTracks()[0])
        .filter((t): t is MediaStreamTrack => !!t && t.muted)
        .map(
          (t) =>
            new Promise<void>((resolve) => {
              const done = () => resolve();
              t.addEventListener('unmute', done, {
                once: true,
                signal: this._abortController.signal,
              });
              setTimeout(done, 3000);
            })
        ),
    ]);
    this._userActivation.markBoundOnce();

    // A newer run owns the tile by now; publishing this run's (older) result
    // would overwrite the newer streams — possibly with null
    if (run !== this._streamsRun) return;

    this.mainStream.set({
      stream: screen ?? cam ?? null,
      isScreenShare: !!screen,
      source: screen ? screenSource : cam ? camSource : null,
    });
    this.pipStream.set((screen && cam) || null);
  }

  private _doubleTap = new DoubleTapTracker();

  // A double tap does what the stage toggle does; fullscreen is only
  // offered by its own button.
  protected onTileClick(event: MouseEvent, user: User) {
    if (!this.stageable()) {
      return;
    }
    if (this._doubleTap.isDoubleTap(event)) {
      this.stageToggled.emit(user);
    }
  }

  swapStreams(user: User) {
    this.streamsSwapped.update((swapped) => !swapped);
    this.handleStreams(user).catch((e) => console.info(e));
  }

  // Only stream changes need the stream handling to re-run.
  private streamsChanged(prev: User, curr: User): boolean {
    return prev.camStream !== curr.camStream || prev.screenCastStream !== curr.screenCastStream;
  }
}
